import React, { useState, useRef, useEffect } from "react";
import { Modal, Checkbox, Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { PHOTO_PRIVACY_SHORT_CONTENT } from "../../utilities/constants/photoPrivacyContent";
import { useNavigate } from "react-router-dom";

interface PhotoPrivacyModalProps {
  visible: boolean;
  onAccept: () => void;
  onDecline?: () => void;
}

const PhotoPrivacyModal: React.FC<PhotoPrivacyModalProps> = ({ visible, onAccept, onDecline }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Reset state when modal opens and check if scrolling is needed
  useEffect(() => {
    if (visible) {
      setHasScrolledToBottom(false);
      setIsChecked(false);

      // Check if content needs scrolling
      setTimeout(() => {
        if (contentRef.current) {
          const { scrollHeight, clientHeight } = contentRef.current;
          // If content doesn't need scrolling, enable the checkbox immediately
          if (scrollHeight <= clientHeight + 10) {
            setHasScrolledToBottom(true);
          }
        }
      }, 100);
    }
  }, [visible]);

  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      // Check if user has scrolled to bottom (with 50px tolerance)
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setHasScrolledToBottom(true);
      }
    }
  };

  const handleAccept = () => {
    onAccept();
  };

  const handleDecline = () => {
    if (onDecline) {
      onDecline();
    }
  };
  const handleClose = () => {
    navigate(-1);
  };

  return (
    <Modal
      open={visible}
      title={null}
      footer={null}
      closable={false}
      maskClosable={false}
      centered
      width={600}
      className="photo-privacy-modal *:*:!p-0 rounded-xl overflow-hidden"
      styles={{
        body: { padding: 0 },
      }}
    >
      <div className="flex flex-col max-h-[90dvh]">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-primary/5">
          <h2 className="m-0 text-xl font-semibold text-gray-900">{PHOTO_PRIVACY_SHORT_CONTENT.title}</h2>
          <button onClick={handleClose} className="flex items-center justify-center w-8 h-8 text-gray-500 transition-colors rounded-lg hover:bg-gray-200 hover:text-gray-700">
            <CloseOutlined className="text-lg" />
          </button>
        </div>

        <div ref={contentRef} onScroll={handleScroll} className="flex-1 p-6 overflow-y-auto" style={{ maxHeight: "60vh" }}>
          <div className="mb-6">
            <p className="mb-4 text-sm leading-relaxed text-gray-700">{PHOTO_PRIVACY_SHORT_CONTENT.introduction}</p>

            <ul className="space-y-3">
              {PHOTO_PRIVACY_SHORT_CONTENT.points.map((point, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-1.5 h-1.5 mt-2 rounded-full bg-primary"></span>
                  <span className="text-sm leading-relaxed text-gray-700">{point}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="p-4 mt-4 rounded-lg bg-gray-50">
            <p className="m-0 text-sm text-gray-600">
              If you have questions, contact us at:{" "}
              <a href={`mailto:${PHOTO_PRIVACY_SHORT_CONTENT.contactEmail}`} className="font-medium text-primary hover:underline">
                {PHOTO_PRIVACY_SHORT_CONTENT.contactEmail}
              </a>
            </p>
          </div>

          <div className="flex items-start gap-3 mt-6 mb-4">
            <Checkbox checked={isChecked} onChange={(e) => setIsChecked(e.target.checked)} disabled={!hasScrolledToBottom} className="mt-1" />
            <label className={`text-sm cursor-pointer select-none ${hasScrolledToBottom ? "text-gray-700" : "text-gray-400"}`} onClick={() => hasScrolledToBottom && setIsChecked(!isChecked)}>
              {PHOTO_PRIVACY_SHORT_CONTENT.confirmText}
            </label>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-50">
          {!hasScrolledToBottom && (
            <div className="flex items-center justify-center gap-2 mb-3 text-sm text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
              <span>Please scroll to the bottom to continue</span>
            </div>
          )}

          <div className="flex gap-3">
            {onDecline && (
              <Button size="large" className="flex-1" onClick={handleDecline}>
                Decline
              </Button>
            )}
            <Button type="primary" size="large" className={`${onDecline ? "flex-1" : "w-full"}`} disabled={!isChecked || !hasScrolledToBottom} onClick={handleAccept}>
              I Agree
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PhotoPrivacyModal;
